/* eslint-disable function-paren-newline */
/* eslint-disable implicit-arrow-linebreak */
/** kware start edit */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import stripesFinalForm from '@folio/stripes/final-form';
import { branding, okapi } from 'stripes-config';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  TextField,
  Button,
  Row,
  Col,
  Headline,
  Icon,
} from '@folio/stripes-components';
import { Field } from 'react-final-form';
import styles from './Login.css';
import OrganizationLogo from '../OrganizationLogo';
import SSOLogin from '../SSOLogin';
import FieldLabel from '../CreateResetPassword/components/FieldLabel';
import AuthErrorsContainer from '../AuthErrorsContainer';

function Login({
  pristine,
  submitting,
  handleSubmit,
  submitSucceeded,
  form,
  ssoActive,
  authErrors,
  handleSSOLogin,
}) {
  const [passwordMasked, setPasswordMasked] = useState(true);
  const cookieMessage = navigator.cookieEnabled ? (
    ''
  ) : (
    <Row center="xs">
      <Col xs={6}>
        <Headline size="large" tag="h3">
          <FormattedMessage id="stripes-core.title.cookieEnabled" />
        </Headline>
      </Col>
    </Row>
  );

  return (
    <main>
      <div className={styles.wrapper} style={branding.style?.login ?? {}}>
        <div className={styles.container}>
          <Row center="xs">
            <Col xs={6}>
              <OrganizationLogo />
            </Col>
          </Row>
          <Row>
            <form
              className={styles.form}
              onSubmit={(data) =>
                handleSubmit(data).then(() =>
                  form.change('password', undefined),
                )
              }
            >
              <Row center="xs">
                <Col xs={6}>
                  <div className={styles.formGroup}>
                    {ssoActive && <SSOLogin handleSSOLogin={handleSSOLogin} />}
                  </div>
                </Col>
              </Row>
              <Row center="xs">
                <Col xs={6}>
                  <Headline size="xx-large" tag="h1" data-test-h1>
                    <FormattedMessage id="stripes-core.title.login" />
                  </Headline>
                </Col>
              </Row>
              {cookieMessage}
              <div data-test-new-username-field>
                <Row center="xs">
                  <Col xs={6}>
                    <Row between="xs" bottom="xs">
                      <Col xs={3}>
                        <FieldLabel htmlFor="input-username">
                          <FormattedMessage id="stripes-core.username" />
                        </FieldLabel>
                      </Col>
                    </Row>
                  </Col>
                </Row>
                <Row center="xs">
                  <Col xs={6}>
                    <Field
                      id="input-username"
                      name="username"
                      type="text"
                      component={TextField}
                      inputClass={styles.input}
                      autoComplete="username"
                      autoCapitalize="none"
                      validationEnabled={false}
                      hasClearIcon={false}
                      marginBottom0
                      fullWidth
                      autoFocus
                    />
                  </Col>
                </Row>
              </div>
              <div data-test-new-username-field>
                <Row center="xs">
                  <Col xs={6}>
                    <Row between="xs" bottom="xs">
                      <Col xs={3}>
                        <FieldLabel htmlFor="input-password">
                          <FormattedMessage id="stripes-core.password" />
                        </FieldLabel>
                      </Col>
                    </Row>
                  </Col>
                </Row>
                <Row center="xs">
                  <Col xs={6}>
                    <Field
                      id="input-password"
                      component={TextField}
                      name="password"
                      type={passwordMasked ? 'password' : 'text'}
                      value=""
                      marginBottom0
                      fullWidth
                      inputClass={styles.input}
                      validationEnabled={false}
                      hasClearIcon={false}
                      autoComplete="current-password"
                      /** -- Adding password unmask feature -- */
                      endControl={
                        <Button
                          buttonStyle="slim"
                          onClick={() => setPasswordMasked(!passwordMasked)}
                          marginBottom0
                        >
                          <Icon
                            icon={passwordMasked ? 'eye-closed' : 'eye-open'}
                            size="large"
                          />
                        </Button>
                      }
                    />
                  </Col>
                </Row>
              </div>
              <Row center="xs">
                <Col xs={6}>
                  <div className={styles.formGroup}>
                    <Button
                      buttonStyle="primary"
                      id="clickable-login"
                      type="submit"
                      buttonClass={styles.submitButton}
                      disabled={
                        submitting ||
                        pristine ||
                        !form.getState().values.username ||
                        !form.getState().values.password
                      }
                      fullWidth
                      marginBottom0
                    >
                      <FormattedMessage
                        id={`stripes-core.${
                          submitting || submitSucceeded ? 'loggingIn' : 'login'
                        }`}
                      />
                    </Button>
                  </div>
                </Col>
              </Row>
              <Row className={styles.linksWrapper} center="xs">
                <Col xs={6}>
                  <Row between="xs">
                    <Col
                      xs={12}
                      sm={6}
                      md={4}
                      data-test-new-forgot-password-link
                    >
                      <Button
                        to="/forgot-password"
                        buttonClass={styles.link}
                        type="button"
                        buttonStyle="link"
                        disabled={submitting}
                      >
                        <FormattedMessage id="stripes-core.button.forgotPassword" />
                      </Button>
                    </Col>
                    <Col
                      xs={12}
                      sm={6}
                      md={4}
                      data-test-new-forgot-username-link
                    >
                      <Button
                        to="/forgot-username"
                        buttonClass={styles.link}
                        type="button"
                        buttonStyle="link"
                        disabled={submitting}
                      >
                        <FormattedMessage id="stripes-core.button.forgotUsername" />
                      </Button>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row center="xs">
                <Col xs={6}>
                  <div className={styles.authErrorsWrapper}>
                    <AuthErrorsContainer errors={authErrors} />
                  </div>
                </Col>
              </Row>
            </form>
            {ssoActive && <form id="ssoForm" />}
          </Row>
        </div>
      </div>
    </main>
  );
}

Login.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool,
  submitting: PropTypes.bool,
  form: PropTypes.object.isRequired,
  ssoActive: PropTypes.bool,
  authErrors: PropTypes.arrayOf(PropTypes.object),
  submitSucceeded: PropTypes.bool,
  handleSSOLogin: PropTypes.func.isRequired,
};

Login.defaultProps = {
  authErrors: [],
  ssoActive: false,
};

export default stripesFinalForm({
  mutators: {},
  validateOnBlur: true,
  subscription: {
    values: true,
  },
})(Login);
